// Comprehensive activity definitions with weather factors
// Each activity has ideal conditions, scoring weights, and tips generation

export const ACTIVITY_CATEGORIES = {
  EXERCISE: 'Exercise & Fitness',
  CYCLING: 'Cycling & Wheels',
  WATER: 'Water Activities',
  NATURE: 'Nature Observation',
  SKY: 'Sky Watching',
  // PHOTOGRAPHY removed - too generic
  YARD: 'Yard Work',
  LAND: 'Land Management',
  GARDEN: 'Gardening',
  SPORTS: 'Sports & Recreation',
  SOCIAL: 'Social & Leisure',
  PETS: 'Pet Activities',
};

// Activity definitions
export const activities = [
  // ===== EXERCISE & FITNESS =====
  {
    id: 'running',
    name: 'Running',
    emoji: '🏃',
    category: ACTIVITY_CATEGORIES.EXERCISE,
    color: 'bg-purple-500',
    tempRange: { ideal: [45, 65], acceptable: [35, 80], poor: [25, 90] },
    maxWind: 20,
    rainTolerance: 'light', // none, light, moderate, any
    humidityMax: 80,
    bestHours: [6, 9, 17, 20], // early morning or evening
    seasonal: { spring: 1, summer: 0.8, fall: 1, winter: 0.7 },
  },
  {
    id: 'walking',
    name: 'Walking',
    emoji: '🚶',
    category: ACTIVITY_CATEGORIES.EXERCISE,
    color: 'bg-green-500',
    tempRange: { ideal: [50, 75], acceptable: [35, 85], poor: [20, 95] },
    maxWind: 25,
    rainTolerance: 'light',
    humidityMax: 90,
    bestHours: [7, 11, 16, 20],
    seasonal: { spring: 1, summer: 0.9, fall: 1, winter: 0.8 },
  },
  {
    id: 'hiking',
    name: 'Hiking',
    emoji: '🥾',
    category: ACTIVITY_CATEGORIES.EXERCISE,
    color: 'bg-orange-500',
    tempRange: { ideal: [50, 70], acceptable: [35, 85], poor: [25, 95] },
    maxWind: 25,
    rainTolerance: 'none',
    humidityMax: 85,
    bestHours: [6, 14],
    seasonal: { spring: 1, summer: 0.8, fall: 1, winter: 0.6 },
  },
  {
    id: 'trail-running',
    name: 'Trail Running',
    emoji: '🏔️',
    category: ACTIVITY_CATEGORIES.EXERCISE,
    color: 'bg-amber-600',
    tempRange: { ideal: [45, 65], acceptable: [35, 75], poor: [25, 85] },
    maxWind: 20,
    rainTolerance: 'none', // trails get slippery
    humidityMax: 75,
    bestHours: [6, 10, 17, 20],
    seasonal: { spring: 0.9, summer: 0.8, fall: 1, winter: 0.5 },
  },
  {
    id: 'outdoor-yoga',
    name: 'Outdoor Yoga',
    emoji: '🧘',
    category: ACTIVITY_CATEGORIES.EXERCISE,
    color: 'bg-teal-500',
    tempRange: { ideal: [60, 78], acceptable: [55, 85], poor: [50, 90] },
    maxWind: 10, // wind is very disruptive
    rainTolerance: 'none',
    humidityMax: 70,
    bestHours: [6, 9, 17, 20],
    seasonal: { spring: 1, summer: 0.9, fall: 1, winter: 0.3 },
  },

  // ===== CYCLING & WHEELS =====
  {
    id: 'road-biking',
    name: 'Road Biking',
    emoji: '🚴',
    category: ACTIVITY_CATEGORIES.CYCLING,
    color: 'bg-blue-500',
    tempRange: { ideal: [55, 75], acceptable: [40, 85], poor: [30, 95] },
    maxWind: 20, // headwinds are brutal
    rainTolerance: 'none', // wet roads dangerous
    humidityMax: 85,
    bestHours: [6, 10, 16, 20],
    seasonal: { spring: 1, summer: 0.9, fall: 1, winter: 0.5 },
  },
  {
    id: 'mountain-biking',
    name: 'Mountain Biking',
    emoji: '🚵',
    category: ACTIVITY_CATEGORIES.CYCLING,
    color: 'bg-amber-700',
    tempRange: { ideal: [50, 70], acceptable: [40, 80], poor: [30, 90] },
    maxWind: 25,
    rainTolerance: 'none', // trails get muddy
    humidityMax: 80,
    bestHours: [7, 11, 15, 19],
    seasonal: { spring: 0.8, summer: 0.9, fall: 1, winter: 0.4 },
  },
  {
    id: 'skateboarding',
    name: 'Skateboarding',
    emoji: '🛹',
    category: ACTIVITY_CATEGORIES.CYCLING,
    color: 'bg-gray-600',
    tempRange: { ideal: [55, 80], acceptable: [45, 90], poor: [35, 100] },
    maxWind: 15,
    rainTolerance: 'none', // wet surfaces dangerous
    humidityMax: 85,
    bestHours: [10, 20],
    seasonal: { spring: 1, summer: 1, fall: 1, winter: 0.5 },
  },

  // ===== WATER ACTIVITIES =====
  {
    id: 'fishing',
    name: 'Fishing',
    emoji: '🎣',
    category: ACTIVITY_CATEGORIES.WATER,
    color: 'bg-cyan-600',
    tempRange: { ideal: [50, 75], acceptable: [35, 85], poor: [25, 95] },
    maxWind: 20,
    rainTolerance: 'light', // light rain can be good
    humidityMax: 95,
    bestHours: [5, 9, 17, 21], // dawn and dusk
    seasonal: { spring: 1, summer: 0.9, fall: 1, winter: 0.7 },
    pressureSensitive: true, // barometric pressure matters
  },
  {
    id: 'fly-fishing',
    name: 'Fly Fishing',
    emoji: '🪰',
    category: ACTIVITY_CATEGORIES.WATER,
    color: 'bg-teal-600',
    tempRange: { ideal: [50, 72], acceptable: [38, 82], poor: [28, 92] },
    maxWind: 12, // casting a fly line in wind is misery
    rainTolerance: 'light',
    humidityMax: 95,
    bestHours: [5, 10, 16, 20], // hatch hours skew to morning/evening
    seasonal: { spring: 1, summer: 0.9, fall: 1, winter: 0.4 },
    pressureSensitive: true,
  },
  {
    id: 'kayaking',
    name: 'Kayaking',
    emoji: '🛶',
    category: ACTIVITY_CATEGORIES.WATER,
    color: 'bg-cyan-500',
    tempRange: { ideal: [60, 80], acceptable: [50, 90], poor: [40, 95] },
    maxWind: 15, // wind affects paddling significantly
    rainTolerance: 'light',
    humidityMax: 95,
    bestHours: [7, 11, 15, 19],
    seasonal: { spring: 0.9, summer: 1, fall: 0.9, winter: 0.3 },
  },
  {
    id: 'motorboating',
    name: 'Motorboating',
    emoji: '🛥️',
    category: ACTIVITY_CATEGORIES.WATER,
    color: 'bg-blue-600',
    tempRange: { ideal: [65, 85], acceptable: [55, 95], poor: [45, 100] },
    maxWind: 15, // chop builds fast on open water (the Hudson especially)
    rainTolerance: 'light',
    humidityMax: 95,
    bestHours: [9, 18],
    seasonal: { spring: 0.7, summer: 1, fall: 0.7, winter: 0 },
    // TODO: tide windows via NOAA CO-OPS API — on the tidal Hudson,
    // slack tide + light wind is the real "go" signal
  },
  {
    id: 'swimming',
    name: 'Swimming',
    emoji: '🏊',
    category: ACTIVITY_CATEGORIES.WATER,
    color: 'bg-blue-400',
    tempRange: { ideal: [75, 90], acceptable: [70, 100], poor: [60, 105] },
    maxWind: 25,
    rainTolerance: 'light',
    humidityMax: 100,
    bestHours: [10, 18],
    seasonal: { spring: 0.5, summer: 1, fall: 0.5, winter: 0.1 },
  },
  {
    id: 'paddleboarding',
    name: 'Paddleboarding',
    emoji: '🏄',
    category: ACTIVITY_CATEGORIES.WATER,
    color: 'bg-teal-400',
    tempRange: { ideal: [70, 85], acceptable: [60, 95], poor: [50, 100] },
    maxWind: 12, // very wind sensitive
    rainTolerance: 'none',
    humidityMax: 95,
    bestHours: [6, 10, 16, 19], // calm morning/evening
    seasonal: { spring: 0.7, summer: 1, fall: 0.7, winter: 0.2 },
  },

  // ===== NATURE OBSERVATION =====
  {
    id: 'birdwatching',
    name: 'Birdwatching',
    emoji: '🦅',
    category: ACTIVITY_CATEGORIES.NATURE,
    color: 'bg-lime-600',
    tempRange: { ideal: [45, 70], acceptable: [30, 85], poor: [20, 95] },
    maxWind: 15, // birds hunker in high wind
    rainTolerance: 'light',
    humidityMax: 90,
    bestHours: [5, 10, 16, 19], // early morning peak
    seasonal: { spring: 1, summer: 0.8, fall: 1, winter: 0.7 },
  },
  {
    id: 'wildlife-watching',
    name: 'Wildlife Watching',
    emoji: '🦌',
    category: ACTIVITY_CATEGORIES.NATURE,
    color: 'bg-emerald-700',
    tempRange: { ideal: [40, 65], acceptable: [25, 80], poor: [15, 90] },
    maxWind: 20,
    rainTolerance: 'light',
    humidityMax: 90,
    bestHours: [5, 8, 17, 20], // dawn and dusk
    seasonal: { spring: 1, summer: 0.7, fall: 1, winter: 0.8 },
  },
  {
    id: 'butterfly-watching',
    name: 'Butterfly Watching',
    emoji: '🦋',
    category: ACTIVITY_CATEGORIES.NATURE,
    color: 'bg-pink-500',
    tempRange: { ideal: [65, 80], acceptable: [60, 90], poor: [55, 95] },
    maxWind: 10, // butterflies can't fly in wind
    rainTolerance: 'none',
    humidityMax: 80,
    bestHours: [10, 15], // midday when warm
    seasonal: { spring: 0.8, summer: 1, fall: 0.6, winter: 0 },
    requiresSun: true,
  },

  // ===== SKY WATCHING (Night Activities) =====
  {
    id: 'stargazing',
    name: 'Stargazing',
    emoji: '🌟',
    category: ACTIVITY_CATEGORIES.SKY,
    color: 'bg-indigo-600',
    tempRange: { ideal: [45, 70], acceptable: [30, 80], poor: [20, 90] },
    maxWind: 20,
    rainTolerance: 'none',
    humidityMax: 70, // haze affects visibility
    bestHours: [21, 4], // night only
    seasonal: { spring: 0.9, summer: 0.8, fall: 1, winter: 1 }, // winter has clearest skies
    nightOnly: true,
    requiresClearSky: true,
    moonSensitive: true, // new moon best
  },
  {
    id: 'aurora-watching',
    name: 'Aurora Watching',
    emoji: '🌌',
    category: ACTIVITY_CATEGORIES.SKY,
    color: 'bg-violet-600',
    tempRange: { ideal: [20, 50], acceptable: [0, 60], poor: [-20, 70] },
    maxWind: 30,
    rainTolerance: 'none',
    humidityMax: 80,
    bestHours: [22, 3], // late night
    seasonal: { spring: 1, summer: 0.5, fall: 1, winter: 1 },
    nightOnly: true,
    requiresClearSky: true,
    requiresAuroraActivity: true, // needs Kp index check
    latitudeSensitive: true, // better at higher latitudes
  },
  {
    id: 'meteor-shower',
    name: 'Meteor Shower',
    emoji: '☄️',
    category: ACTIVITY_CATEGORIES.SKY,
    color: 'bg-purple-700',
    tempRange: { ideal: [40, 70], acceptable: [25, 80], poor: [15, 90] },
    maxWind: 25,
    rainTolerance: 'none',
    humidityMax: 70,
    bestHours: [0, 5], // after midnight best
    seasonal: { spring: 0.8, summer: 1, fall: 1, winter: 0.9 },
    nightOnly: true,
    requiresClearSky: true,
    moonSensitive: true,
    eventBased: true, // only show during meteor shower dates
  },

  // ===== YARD WORK =====
  // maintenanceOnly: true — these are weekly/seasonal windows, not day-specific cards
  {
    id: 'lawn-mowing',
    maintenanceOnly: true,
    name: 'Lawn Mowing',
    emoji: '🌿',
    category: ACTIVITY_CATEGORIES.YARD,
    color: 'bg-green-600',
    tempRange: { ideal: [55, 80], acceptable: [45, 90], poor: [35, 100] },
    maxWind: 15,
    rainTolerance: 'none', // wet grass clogs mower
    humidityMax: 75, // grass should be dry
    bestHours: [9, 11, 16, 19],
    seasonal: { spring: 1, summer: 1, fall: 0.8, winter: 0.1 },
    requiresDryGrass: true,
  },
  {
    id: 'leaf-raking',
    maintenanceOnly: true,
    name: 'Leaf Raking',
    emoji: '🍂',
    category: ACTIVITY_CATEGORIES.YARD,
    color: 'bg-amber-500',
    tempRange: { ideal: [45, 65], acceptable: [35, 75], poor: [25, 85] },
    maxWind: 8, // wind scatters leaves
    rainTolerance: 'none', // wet leaves heavy
    humidityMax: 70,
    bestHours: [9, 17],
    seasonal: { spring: 0.3, summer: 0.1, fall: 1, winter: 0.2 },
  },
  {
    id: 'pressure-washing',
    maintenanceOnly: true,
    name: 'Pressure Washing',
    emoji: '💦',
    category: ACTIVITY_CATEGORIES.YARD,
    color: 'bg-blue-600',
    tempRange: { ideal: [50, 80], acceptable: [40, 90], poor: [32, 100] },
    maxWind: 20,
    rainTolerance: 'none',
    humidityMax: 80,
    bestHours: [8, 16], // needs time to dry
    seasonal: { spring: 1, summer: 1, fall: 1, winter: 0.3 },
  },
  {
    id: 'gutter-cleaning',
    maintenanceOnly: true,
    name: 'Gutter Cleaning',
    emoji: '🏠',
    category: ACTIVITY_CATEGORIES.YARD,
    color: 'bg-slate-500',
    tempRange: { ideal: [50, 75], acceptable: [40, 85], poor: [30, 95] },
    maxWind: 15, // ladder safety
    rainTolerance: 'none',
    humidityMax: 80,
    bestHours: [9, 16],
    seasonal: { spring: 0.8, summer: 0.5, fall: 1, winter: 0.3 },
  },

  // ===== LAND MANAGEMENT =====
  {
    id: 'firewood',
    maintenanceOnly: true,
    name: 'Firewood Work',
    emoji: '🪓',
    category: ACTIVITY_CATEGORIES.LAND,
    color: 'bg-amber-800',
    tempRange: { ideal: [35, 60], acceptable: [25, 75], poor: [15, 85] },
    maxWind: 20,
    rainTolerance: 'none',
    humidityMax: 80,
    bestHours: [8, 17],
    seasonal: { spring: 0.8, summer: 0.5, fall: 1, winter: 0.9 },
  },
  {
    id: 'burn-pile',
    maintenanceOnly: true,
    name: 'Burn Pile',
    emoji: '🔥',
    category: ACTIVITY_CATEGORIES.LAND,
    color: 'bg-red-600',
    tempRange: { ideal: [40, 65], acceptable: [30, 75], poor: [20, 85] },
    maxWind: 10, // critical for fire safety
    rainTolerance: 'none',
    humidityMin: 30, // too dry is dangerous
    humidityMax: 70,
    bestHours: [9, 16],
    seasonal: { spring: 0.9, summer: 0.3, fall: 1, winter: 0.7 },
    fireSafetyCritical: true,
  },
  {
    id: 'brush-clearing',
    maintenanceOnly: true,
    name: 'Brush Clearing',
    emoji: '🌳',
    category: ACTIVITY_CATEGORIES.LAND,
    color: 'bg-green-700',
    tempRange: { ideal: [45, 65], acceptable: [35, 80], poor: [25, 90] },
    maxWind: 20,
    rainTolerance: 'none',
    humidityMax: 80,
    bestHours: [8, 17],
    seasonal: { spring: 1, summer: 0.6, fall: 1, winter: 0.8 },
  },
  {
    id: 'invasive-removal',
    maintenanceOnly: true,
    name: 'Invasive Species',
    emoji: '🌾',
    category: ACTIVITY_CATEGORIES.LAND,
    color: 'bg-lime-700',
    tempRange: { ideal: [50, 70], acceptable: [40, 80], poor: [30, 90] },
    maxWind: 20,
    rainTolerance: 'light', // easier to pull after rain
    humidityMax: 85,
    bestHours: [8, 17],
    seasonal: { spring: 1, summer: 0.7, fall: 0.9, winter: 0.5 },
  },

  // ===== GARDENING =====
  {
    id: 'planting',
    maintenanceOnly: true,
    name: 'Planting',
    emoji: '🌱',
    category: ACTIVITY_CATEGORIES.GARDEN,
    color: 'bg-green-500',
    tempRange: { ideal: [55, 75], acceptable: [45, 85], poor: [35, 95] },
    maxWind: 15,
    rainTolerance: 'none', // soil gets compacted
    humidityMax: 80,
    bestHours: [7, 11, 16, 19],
    seasonal: { spring: 1, summer: 0.7, fall: 0.8, winter: 0.2 },
    soilTempSensitive: true,
  },
  {
    id: 'watering',
    maintenanceOnly: true,
    name: 'Watering',
    emoji: '💧',
    category: ACTIVITY_CATEGORIES.GARDEN,
    color: 'bg-blue-400',
    tempRange: { ideal: [50, 80], acceptable: [40, 90], poor: [30, 100] },
    maxWind: 20,
    rainTolerance: 'none', // don't water if rain coming
    humidityMax: 90,
    bestHours: [5, 9], // early morning best
    seasonal: { spring: 1, summer: 1, fall: 0.8, winter: 0.3 },
  },
  {
    id: 'harvesting',
    maintenanceOnly: true,
    name: 'Harvesting',
    emoji: '🥕',
    category: ACTIVITY_CATEGORIES.GARDEN,
    color: 'bg-orange-500',
    tempRange: { ideal: [55, 80], acceptable: [45, 90], poor: [35, 100] },
    maxWind: 20,
    rainTolerance: 'none',
    humidityMax: 85,
    bestHours: [6, 10], // morning for crispness
    seasonal: { spring: 0.5, summer: 1, fall: 1, winter: 0.2 },
  },

  // ===== SPORTS & RECREATION =====
  {
    id: 'skiing',
    name: 'Skiing',
    emoji: '⛷️',
    category: ACTIVITY_CATEGORIES.SPORTS,
    color: 'bg-sky-500',
    tempRange: { ideal: [15, 32], acceptable: [0, 40], poor: [-10, 50] },
    maxWind: 25,
    rainTolerance: 'none', // rain on snow is miserable
    humidityMax: 95,
    bestHours: [9, 16], // lifts open hours
    seasonal: { spring: 0.6, summer: 0, fall: 0.3, winter: 1 },
    prefersSnow: true, // snow is ideal
  },
  {
    id: 'snowboarding',
    name: 'Snowboarding',
    emoji: '🏂',
    category: ACTIVITY_CATEGORIES.SPORTS,
    color: 'bg-indigo-500',
    tempRange: { ideal: [15, 32], acceptable: [0, 40], poor: [-10, 50] },
    maxWind: 25,
    rainTolerance: 'none',
    humidityMax: 95,
    bestHours: [9, 16],
    seasonal: { spring: 0.6, summer: 0, fall: 0.3, winter: 1 },
    prefersSnow: true,
  },
  {
    id: 'cross-country-skiing',
    name: 'Cross-Country Skiing',
    emoji: '🎿',
    category: ACTIVITY_CATEGORIES.SPORTS,
    color: 'bg-cyan-600',
    tempRange: { ideal: [10, 30], acceptable: [-5, 38], poor: [-20, 45] },
    maxWind: 20,
    rainTolerance: 'none',
    humidityMax: 90,
    bestHours: [8, 17],
    seasonal: { spring: 0.5, summer: 0, fall: 0.2, winter: 1 },
    prefersSnow: true,
  },
  {
    id: 'tennis',
    name: 'Tennis',
    emoji: '🎾',
    category: ACTIVITY_CATEGORIES.SPORTS,
    color: 'bg-lime-500',
    tempRange: { ideal: [60, 80], acceptable: [50, 90], poor: [40, 100] },
    maxWind: 15, // affects ball flight
    rainTolerance: 'none',
    humidityMax: 80,
    bestHours: [8, 11, 16, 20],
    seasonal: { spring: 1, summer: 0.9, fall: 1, winter: 0.5 },
  },
  {
    id: 'basketball',
    name: 'Basketball',
    emoji: '🏀',
    category: ACTIVITY_CATEGORIES.SPORTS,
    color: 'bg-orange-500',
    tempRange: { ideal: [55, 80], acceptable: [45, 90], poor: [35, 98] },
    maxWind: 20, // outdoor hoops tolerate more wind than racket sports
    rainTolerance: 'none',
    humidityMax: 85,
    bestHours: [9, 12, 16, 20],
    seasonal: { spring: 1, summer: 0.9, fall: 1, winter: 0.4 },
  },
  {
    id: 'soccer',
    name: 'Soccer',
    emoji: '⚽',
    category: ACTIVITY_CATEGORIES.SPORTS,
    color: 'bg-emerald-500',
    tempRange: { ideal: [50, 75], acceptable: [40, 88], poor: [30, 95] },
    maxWind: 25, // playable in more wind than racket sports
    rainTolerance: 'light',
    humidityMax: 90,
    bestHours: [9, 12, 16, 19],
    seasonal: { spring: 1, summer: 0.8, fall: 1, winter: 0.3 },
  },
  {
    id: 'golf',
    name: 'Golf',
    emoji: '⛳',
    category: ACTIVITY_CATEGORIES.SPORTS,
    color: 'bg-green-600',
    tempRange: { ideal: [60, 80], acceptable: [50, 90], poor: [40, 100] },
    maxWind: 20,
    rainTolerance: 'none',
    humidityMax: 85,
    bestHours: [7, 11, 14, 18],
    seasonal: { spring: 1, summer: 0.9, fall: 1, winter: 0.4 },
  },
  {
    id: 'disc-golf',
    name: 'Disc Golf',
    emoji: '🥏',
    category: ACTIVITY_CATEGORIES.SPORTS,
    color: 'bg-blue-500',
    tempRange: { ideal: [55, 80], acceptable: [45, 90], poor: [35, 100] },
    maxWind: 15, // wind affects disc flight
    rainTolerance: 'light',
    humidityMax: 85,
    bestHours: [8, 19],
    seasonal: { spring: 1, summer: 1, fall: 1, winter: 0.6 },
  },

  // ===== SOCIAL & LEISURE =====
  {
    id: 'picnic',
    name: 'Picnic',
    emoji: '🧺',
    category: ACTIVITY_CATEGORIES.SOCIAL,
    color: 'bg-rose-400',
    tempRange: { ideal: [65, 80], acceptable: [55, 90], poor: [45, 100] },
    maxWind: 15,
    rainTolerance: 'none',
    humidityMax: 75,
    bestHours: [11, 15, 17, 20],
    seasonal: { spring: 1, summer: 1, fall: 1, winter: 0.3 },
  },
  {
    id: 'bbq',
    name: 'BBQ / Grilling',
    emoji: '🍖',
    category: ACTIVITY_CATEGORIES.SOCIAL,
    color: 'bg-red-500',
    tempRange: { ideal: [60, 85], acceptable: [50, 95], poor: [40, 105] },
    maxWind: 20,
    rainTolerance: 'none',
    humidityMax: 85,
    bestHours: [11, 20],
    seasonal: { spring: 1, summer: 1, fall: 1, winter: 0.5 },
  },
  {
    id: 'camping',
    name: 'Camping',
    emoji: '⛺',
    category: ACTIVITY_CATEGORIES.SOCIAL,
    color: 'bg-emerald-600',
    tempRange: { ideal: [50, 75], acceptable: [35, 85], poor: [25, 95] },
    maxWind: 25,
    rainTolerance: 'light',
    humidityMax: 85,
    bestHours: [0, 24], // all day activity
    seasonal: { spring: 0.9, summer: 1, fall: 1, winter: 0.4 },
  },
  {
    id: 'beach',
    name: 'Beach Day',
    emoji: '🏖️',
    category: ACTIVITY_CATEGORIES.SOCIAL,
    color: 'bg-yellow-400',
    tempRange: { ideal: [75, 90], acceptable: [70, 100], poor: [60, 105] },
    maxWind: 20,
    rainTolerance: 'none',
    humidityMax: 90,
    bestHours: [9, 17],
    seasonal: { spring: 0.5, summer: 1, fall: 0.5, winter: 0.1 },
    uvSensitive: true,
  },

  // ===== PET ACTIVITIES =====
  {
    id: 'dog-walking',
    name: 'Dog Walking',
    emoji: '🐕',
    category: ACTIVITY_CATEGORIES.PETS,
    color: 'bg-amber-400',
    tempRange: { ideal: [45, 75], acceptable: [30, 85], poor: [20, 95] },
    maxWind: 25,
    rainTolerance: 'light',
    humidityMax: 85,
    bestHours: [6, 9, 17, 21],
    seasonal: { spring: 1, summer: 0.8, fall: 1, winter: 0.8 },
    pavementTempSensitive: true, // hot pavement danger
  },
  {
    id: 'dog-park',
    name: 'Dog Park',
    emoji: '🐶',
    category: ACTIVITY_CATEGORIES.PETS,
    color: 'bg-orange-400',
    tempRange: { ideal: [50, 75], acceptable: [35, 85], poor: [25, 95] },
    maxWind: 20,
    rainTolerance: 'none', // muddy parks no fun
    humidityMax: 80,
    bestHours: [7, 10, 16, 20],
    seasonal: { spring: 1, summer: 0.8, fall: 1, winter: 0.7 },
  },
];

// Meteor shower dates (approximate peak dates each year)
export const meteorShowers = [
  { name: 'Quadrantids', peakMonth: 1, peakDay: 3, duration: 3 },
  { name: 'Lyrids', peakMonth: 4, peakDay: 22, duration: 3 },
  { name: 'Eta Aquariids', peakMonth: 5, peakDay: 6, duration: 5 },
  { name: 'Perseids', peakMonth: 8, peakDay: 12, duration: 5 },
  { name: 'Orionids', peakMonth: 10, peakDay: 21, duration: 3 },
  { name: 'Leonids', peakMonth: 11, peakDay: 17, duration: 3 },
  { name: 'Geminids', peakMonth: 12, peakDay: 14, duration: 5 },
];

// Helper to get current season
export function getCurrentSeason(month) {
  if (month >= 3 && month <= 5) return 'spring';
  if (month >= 6 && month <= 8) return 'summer';
  if (month >= 9 && month <= 11) return 'fall';
  return 'winter';
}

// Check if meteor shower is active
export function getActiveMeteorShower(date = new Date()) {
  const month = date.getMonth() + 1;
  const day = date.getDate();

  for (const shower of meteorShowers) {
    const startDay = shower.peakDay - Math.floor(shower.duration / 2);
    const endDay = shower.peakDay + Math.floor(shower.duration / 2);

    if (month === shower.peakMonth && day >= startDay && day <= endDay) {
      return shower;
    }
  }
  return null;
}

// Calculate moon phase (0 = new moon, 0.5 = full moon, 1 = new moon)
export function getMoonPhase(date = new Date()) {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  // Simplified calculation - accurate to ~1 day
  const c = Math.floor(365.25 * year);
  const e = Math.floor(30.6 * month);
  const jd = c + e + day - 694039.09; // Julian date offset
  const phase = jd / 29.53058867; // Synodic month

  return phase - Math.floor(phase); // 0-1 value
}

// Get moon phase description
export function getMoonPhaseDescription(phase) {
  if (phase < 0.0625 || phase >= 0.9375) return 'New Moon';
  if (phase < 0.1875) return 'Waxing Crescent';
  if (phase < 0.3125) return 'First Quarter';
  if (phase < 0.4375) return 'Waxing Gibbous';
  if (phase < 0.5625) return 'Full Moon';
  if (phase < 0.6875) return 'Waning Gibbous';
  if (phase < 0.8125) return 'Last Quarter';
  return 'Waning Crescent';
}

// Check if it's currently dark (for night activities)
export function isDarkOutside(sunrise, sunset) {
  const now = new Date();
  const currentHour = now.getHours() + now.getMinutes() / 60;

  // Simple check - is it before sunrise or after sunset?
  // sunrise/sunset should be hour values (e.g., 6.5 for 6:30 AM)
  return currentHour < sunrise || currentHour > sunset;
}
